import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAtom } from "jotai";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Intent, IntentSentence } from "@/model";
import { intentTableRefetchAtom, intentTableScrollToIntentIdAtom } from "@/atom/intentTable";

import IntentTableRowFactory, { IntentTableRowFactoryProps } from "./IntentTableRowFactory";


interface IntentTableVirtualizerProps {
    intents: Intent[];
    selectedIntentIds: string[];
    showIntentIds: string[];
    openIntentIds: string[];
    onCheck: (id: string, checked: boolean) => void;
    onClickOpen: (id: string, open: boolean) => void;
}

const IntentTableVirtualizer: React.FC<IntentTableVirtualizerProps> = (props) => {
    const { intents, showIntentIds, openIntentIds, selectedIntentIds, onCheck, onClickOpen } = props;

    const parentRef = useRef<HTMLDivElement>(null);
    const [rows, setRows] = useState<IntentTableRowFactoryProps[]>([]);

    const [intentTableRefetch, setIntentTableRefetch] = useAtom(intentTableRefetchAtom);
    const [scrollToIntentId, setScrollToIntentId] = useAtom(intentTableScrollToIntentIdAtom);

    const getRows = useCallback(() => {
        const toIntentRow = (intent: Intent): IntentTableRowFactoryProps => {
            return {
                type: 'intent',
                intent: intent,
                onCheck: onCheck,
                checked: selectedIntentIds.includes(intent.id),
                open: openIntentIds.includes(intent.id),
                onClickOpen: onClickOpen
            };
        };
        const toSentenceEditRow = (intent: Intent): IntentTableRowFactoryProps => {
            return {
                type: 'sentence-edit',
                intent: intent
            };
        };
        const toSentenceRow = (intent: Intent, sentence: IntentSentence, index: number): IntentTableRowFactoryProps => {
            return {
                type: 'sentence',
                sentence: sentence,
                activeIntent: intent.active,
                isLast: index === intent.sentences.length - 1
            };
        };

        const showIntents = intents!.filter(intent => showIntentIds.includes(intent.id));
        const rows: IntentTableRowFactoryProps[] = [];
        showIntents.forEach(intent => {
            rows.push(toIntentRow(intent));
            if (openIntentIds.includes(intent.id)) {
                if (intent.active) {
                    rows.push(toSentenceEditRow(intent));
                }
                intent.sentences.forEach((sentence, index) => {
                    rows.push(toSentenceRow(intent, sentence, index));
                });
            }
        });
        return rows;
    }, [intents, showIntentIds, openIntentIds, onCheck, selectedIntentIds, onClickOpen]);

    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => parentRef.current,
        estimateSize: index => {
            const row = rows[index];

            switch (row.type) {
                case 'intent':
                    return 45;
                case 'sentence':
                    return 45;
                case 'sentence-edit':
                    return 51;
            }
        },
        overscan: 5
    });

    useEffect(() => {
        if (!scrollToIntentId) {
            return;
        }
        const index = rows.findIndex(
            item => item.type === "intent"
                && item.intent.id === scrollToIntentId
                && openIntentIds.includes(item.intent.id)
        );
        if (index === -1) {
            return;
        }

        rowVirtualizer.scrollToIndex(index, {
            align: 'start',
        });
        setScrollToIntentId(undefined);
        // eslint-disable-next-line
    }, [scrollToIntentId, rows, openIntentIds]);

    useEffect(() => {
        setRows(getRows());
    }, [getRows]);

    useEffect(() => {
        if (!intentTableRefetch) {
            return;
        }
        setIntentTableRefetch(false);
        setRows(getRows());
        // eslint-disable-next-line
    }, [intentTableRefetch, setIntentTableRefetch])

    return (
        <div ref={parentRef}
             className="overflow-auto"
             tabIndex={-1}
        >
            <div role="rowgroup"
                 className="relative w-full"
                 style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
            >
                {rowVirtualizer.getVirtualItems().map((virtualRow) => (
                    <div key={virtualRow.index}
                         className="absolute top-0 left-0 w-full"
                         style={{
                             height: `${virtualRow.size}px`,
                             transform: `translateY(${virtualRow.start}px)`,
                         }}
                    >
                        <IntentTableRowFactory {...rows[virtualRow.index]} />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default IntentTableVirtualizer;